import React, { useState } from 'react';
import { Modal, Form, Input, Checkbox, Alert, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelect } from '@refinedev/antd';
import { contactService } from '../../../services/api/contactService';
import { businessPositionService } from '../../../services/api/businessPositionService';
import type { ContactFormData } from '../../../types/contact';
import { PhoneInput } from '../../../components/PhoneInput';
import { CreatableSelect } from '../../../components/CreatableSelect';

const { TextArea } = Input;

interface CreateContactModalProps {
  visible: boolean;
  producerId: number;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * CreateContactModal Component
 * 
 * Modal for creating a new contact for a producer.
 * Validates that at least one contact method (email, phone, or ID) is provided.
 * 
 * Features:
 * - Form validation with required fields
 * - Business position selector
 * - Main contact and favorite checkboxes
 * - Notes textarea
 * - Error handling with server validation messages
 */
export const CreateContactModal: React.FC<CreateContactModalProps> = ({
  visible,
  producerId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ContactFormData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch business positions for the select dropdown
  const { selectProps: positionSelectProps } = useSelect({
    resource: 'business-positions',
    optionLabel: 'name',
    optionValue: 'id',
  });

  /**
   * Handle form submission
   * Validates and creates the contact via API
   */
  const handleSubmit = async (values: ContactFormData) => {
    setLoading(true);
    setError(null);

    try {
      // Create contact via API
      await contactService.createProducerContact(producerId, values);
      
      // Reset form and close modal
      form.resetFields();
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating contact:', err);
      
      // Handle validation errors from backend
      if (err.validationErrors) {
        // Set form field errors
        const formErrors = Object.entries(err.validationErrors).map(([field, messages]) => ({
          name: field as keyof ContactFormData,
          errors: Array.isArray(messages) ? messages : [messages],
        }));
        form.setFields(formErrors);
      } else {
        setError(err.message || t('contacts.error.create'));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal cancel
   * Resets form and closes modal
   */
  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onClose();
  };

  /**
   * Custom validator to ensure at least one contact method is provided
   */
  const validateContactMethod = () => {
    const email = form.getFieldValue('email');
    const mainPhone = form.getFieldValue('main_phone');

    if (!email && !mainPhone) {
      return Promise.reject(new Error(t('contacts.error.at_least_one_contact')));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      title={t('contacts.create.title')}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      confirmLoading={loading}
      width={600}
    >
      {error && (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        {/* Row 1: Nombre / Apellidos */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('contacts.field.name')}
              rules={[
                {
                  required: true,
                  message: `${t('contacts.field.name')} ${t('common.required')}`,
                },
                {
                  whitespace: true,
                  message: `${t('contacts.field.name')} ${t('common.required')}`,
                },
              ]}
            >
              <Input
                placeholder={t('contacts.placeholder.name')}
                maxLength={200}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="surname"
              label={t('contacts.field.surname')}
              rules={[
                {
                  required: true,
                  message: `${t('contacts.field.surname')} ${t('common.required')}`,
                },
                {
                  whitespace: true,
                  message: `${t('contacts.field.surname')} ${t('common.required')}`,
                },
              ]}
            >
              <Input
                placeholder={t('contacts.placeholder.surname')}
                maxLength={200}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: Cargo / Email */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="position_id"
              label={t('contacts.field.position')}
            >
              <CreatableSelect
                {...positionSelectProps}
                placeholder={`${t('common.select')} ${t('contacts.field.position')}`}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onCreateNew={async (name) => {
                  const newPosition = await businessPositionService.createPosition(name);
                  return newPosition;
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="email"
              label={t('contacts.field.email')}
              rules={[
                {
                  type: 'email',
                  message: t('pages.login.errors.validEmail'),
                },
                {
                  validator: validateContactMethod,
                },
              ]}
            >
              <Input
                type="email"
                placeholder={t('contacts.placeholder.email')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: Teléfono 1 / Teléfono 2 */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="main_phone"
              label={t('contacts.field.main_phone')}
              rules={[
                {
                  validator: validateContactMethod,
                },
              ]}
            >
              <PhoneInput
                placeholder={t('contacts.placeholder.main_phone')}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="secondary_phone"
              label={t('contacts.field.secondary_phone')}
            >
              <PhoneInput
                placeholder={t('contacts.placeholder.secondary_phone')}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Favorito */}
        <Form.Item
          name="is_favorite"
          valuePropName="checked"
          initialValue={false}
          style={{ marginBottom: 16 }}
        >
          <Checkbox>
            {t('contacts.field.is_favorite')}
          </Checkbox>
        </Form.Item>

        {/* Notes - Optional */}
        <Form.Item
          name="notes"
          label={t('contacts.field.notes')}
        >
          <TextArea
            rows={4}
            placeholder={t('contacts.field.notes')}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateContactModal;
